/*

& StandaloneFilterChip: a quick-filter chip that turns a filter on and off (20 Oct 2026, the
& app's standalone BeegruSelectChip).

  A quick-filter row has no "All" chip: nothing on means everything shows. A click turns the
  chip on; a click on the one that is on turns it off, which is the caller's onClick to decide
  (pass the "all" value back). While it is on, a ✕ trails the label: the chip widens from the
  label's end to reveal it while the ✕ fades up from 60%, one 250ms ease-in-out clock with no
  overshoot, and turning it off reverses it. The ✕ is only a mark with no click of its own.
  Under reduced motion it only fades. Used by the chat list and the Notifications page.

*/

import { styled, useTheme } from "@mui/system";
import React from "react";

/* The ✕'s room and fade: closed at rest, open while the chip is on. */
const ChipClear = styled("span")({
	display: "inline-flex",
	alignItems: "center",
	overflow: "hidden",
	maxWidth: 0,
	marginLeft: 0,
	opacity: 0,
	transform: "scale(0.6)",
	transition:
		"max-width 250ms ease-in-out, margin-left 250ms ease-in-out, opacity 250ms ease-in-out, transform 250ms ease-in-out",
	"&[data-on='true']": { maxWidth: "0.875rem", marginLeft: "0.375rem", opacity: 1, transform: "scale(1)" },
	"@media (prefers-reduced-motion: reduce)": { transition: "opacity 150ms ease-in-out" },
});

/** A count beside the label (the unread total), in the chip's own ink. */
const ChipCount = styled("span")({
	marginLeft: "0.375rem",
});

const StandaloneFilterChip = ({
	label,
	selected,
	onClick,
	count,
}: {
	label: string;
	selected: boolean;
	onClick: () => void;
	/* Shown after the label when above zero. */
	count?: number;
}) => {
	const theme: any = useTheme();
	return (
		<button
			type="button"
			aria-pressed={selected}
			onClick={onClick}
			style={{
				flexShrink: 0,
				display: "inline-flex",
				alignItems: "center",
				height: "1.875rem",
				padding: "0rem 0.875rem",
				borderRadius: "999px",
				border: selected ? "1px solid transparent" : "1px solid #E0E0E0",
				background: selected ? theme.palette.primary.main : "#FFFFFF",
				color: selected ? "#FFFFFF" : theme.palette.text.primary,
				fontSize: "0.8125rem",
				fontWeight: selected ? 600 : 500,
				cursor: "pointer",
				transition: "background 180ms ease-in-out, color 180ms ease-in-out",
			}}
		>
			{label}
			{count !== undefined && count > 0 ? <ChipCount>{count > 99 ? "99+" : count}</ChipCount> : null}
			<ChipClear
				data-on={selected ? "true" : "false"}
				aria-hidden="true"
			>
				<svg
					viewBox="0 0 24 24"
					width="14"
					height="14"
					fill="currentColor"
				>
					<path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
				</svg>
			</ChipClear>
		</button>
	);
};

export default StandaloneFilterChip;
